import React, { useState, useEffect, useMemo } from "react";
import Handlebars from "handlebars";

// Default test data for preview
const getPreviewData = (organizationName, fundName, supportEmail, message, customSubject, useCustomContent, hasAttachment, attachmentFilename) => ({
  first_name: "John",
  last_name: "Doe",
  full_name: "John Doe",
  organization_name: organizationName || "Sample Organization",
  support_email: supportEmail || "support@example.com",
  fund_name: fundName || "Membership Dues",
  fund_id: "123",
  fund_user_id: "456",
  invoice_id: "789",
  amount_due: "$50.00",
  due_date: "January 15th, 2026",
  payment_link: "https://unionly.io/profile",
  logo_url: "http://cdn.mcauto-images-production.sendgrid.net/fc0a5f8d6c9b25c4/ac417efd-5f84-443c-a0e7-24ecf58bb273/615x342.png",
  invoice_status: "unpaid",
  use_custom_content: useCustomContent || false,
  message: useCustomContent ? (message || "") : "",
  custom_subject: useCustomContent ? (customSubject || "") : "",
  has_attachment: hasAttachment || false,
  attachment_url: hasAttachment ? "#" : "",
  attachment_filename: attachmentFilename || "",
  current_year: new Date().getFullYear(),
});

const EmailPreviewCompact = ({
  fetchTemplateUrl,
  reminderType,
  organizationName,
  fundName,
  supportEmail,
  message,
  customSubject,
  useCustomContent,
  hasAttachment,
  attachmentFilename,
  height = "400px",
}) => {
  const [templateHtml, setTemplateHtml] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch template on mount
  useEffect(() => {
    const fetchTemplate = async () => {
      setIsLoading(true);
      setError(null);

      const url = reminderType
        ? `${fetchTemplateUrl}?type=${reminderType}`
        : fetchTemplateUrl;

      try {
        const response = await fetch(url, {
          headers: { Accept: "application/json" },
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();

        if (data.success) {
          setTemplateHtml(data.html);
        } else {
          setError(data.error || "Failed to fetch template");
        }
      } catch (err) {
        console.error("Error fetching template:", err);
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };

    if (fetchTemplateUrl) {
      fetchTemplate();
    }
  }, [fetchTemplateUrl, reminderType]);

  // Compile and render the template
  const renderedHtml = useMemo(() => {
    if (!templateHtml) return null;

    try {
      const template = Handlebars.compile(templateHtml);
      const data = getPreviewData(
        organizationName,
        fundName,
        supportEmail,
        message,
        customSubject,
        useCustomContent,
        hasAttachment,
        attachmentFilename
      );
      return template(data);
    } catch (err) {
      console.error("Error rendering template:", err);
      return null;
    }
  }, [templateHtml, organizationName, fundName, supportEmail, message, customSubject, useCustomContent, hasAttachment, attachmentFilename]);

  if (isLoading) {
    return (
      <div
        className="flex items-center justify-center bg-gray-50 rounded-lg border border-gray-200"
        style={{ height }}
      >
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-2 text-sm text-gray-500">Loading preview...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div
        className="flex items-center justify-center bg-red-50 rounded-lg border border-red-200"
        style={{ height }}
      >
        <div className="text-center px-4">
          <svg
            className="w-8 h-8 text-red-400 mx-auto"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <p className="mt-2 text-sm text-red-600">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="bg-white rounded-lg border border-gray-200 overflow-hidden"
      style={{ height }}
    >
      <iframe
        srcDoc={renderedHtml}
        title="Email Preview"
        className="w-full h-full"
        sandbox="allow-same-origin"
        style={{ border: "none" }}
      />
    </div>
  );
};

export default EmailPreviewCompact;
